import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { FaLink, FaShieldAlt, FaPlus, FaTimes, FaCheck, FaRoute, FaSave } from "react-icons/fa";

import { useRoutePoints, toLocationString } from "./hooks/useRoutePoints";
import { useRouteCalculation } from "./hooks/useRouteCalculation";
import RouteMap from "./components/RouteMap";
import GenerateFenceModal from "./components/GenerateFenceModal";
import { ToastComponent } from "../Components/ToastComponent";
import { controlService } from "../../Services/control.service";
import { routingService } from "../../Services/routing.service";
import { appendTravelModeToGoogleMapsUrl } from "./utils/googleMaps";

const TYPES = { ADDRESS: "address", COORDINATE: "coordinate", VISIT_POINT: "visitPoint" };
const DEFAULT_VEHICLE_TYPE = "Drive";

const normalizeVehicleTypeOption = (item) => {
  if (typeof item === "string") {
    const value = item.trim();
    return value ? { value, label: value } : null;
  }
  if (!item || typeof item !== "object") return null;
  const value =
    item.value ?? item.Value ?? item.vehicleType ?? item.VehicleType ??
    item.code ?? item.Code ?? item.name ?? item.Name ??
    item.description ?? item.Description;
  if (!value) return null;
  const label =
    item.label ?? item.Label ?? item.description ?? item.Description ??
    item.title ?? item.Title ?? value;
  return { value: String(value), label: String(label) };
};

const normalizeVehicleTypeOptions = (response) => {
  const items = Array.isArray(response)
    ? response
    : Array.isArray(response?.items) ? response.items
    : Array.isArray(response?.data) ? response.data
    : response ? [response] : [];
  const options = items.map(normalizeVehicleTypeOption).filter(Boolean);
  return options.length ? options : [{ value: DEFAULT_VEHICLE_TYPE, label: DEFAULT_VEHICLE_TYPE }];
};

const normalizeVehicleTypeSelection = (value) => {
  if (typeof value !== "string") return DEFAULT_VEHICLE_TYPE;
  const trimmed = value.trim();
  return trimmed || DEFAULT_VEHICLE_TYPE;
};

// ─── Google Autocomplete Input ────────────────────────────────────────────────
function AddressInput({ id, onPlaceSelected }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    let retryTimeout;
    const init = () => {
      if (!window.google?.maps?.places || !inputRef.current) {
        retryTimeout = setTimeout(init, 200);
        return;
      }
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ["geometry", "formatted_address"],
        componentRestrictions: { country: ["br"] },
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.geometry?.location) {
          const address = place.formatted_address || place.name || "";
          if (inputRef.current) inputRef.current.value = address;
          onPlaceSelected?.({ address, latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() });
        }
      });
    };
    init();
    return () => {
      clearTimeout(retryTimeout);
      if (autocompleteRef.current)
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      className="form-control"
      placeholder="Digite o endereço completo…"
      autoComplete="off"
      style={{ fontSize: 13 }}
    />
  );
}

// ─── Type Selector Pills ──────────────────────────────────────────────────────
function TypePills({ value, onChange, t }) {
  const opts = [
    { val: TYPES.ADDRESS, label: t("ROUTING.TYPE_ADDRESS") },
    { val: TYPES.COORDINATE, label: t("ROUTING.TYPE_COORDINATE") },
    { val: TYPES.VISIT_POINT, label: t("ROUTING.TYPE_VISIT_POINT") },
  ];
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {opts.map((o) => (
        <button
          key={o.val}
          type="button"
          onClick={() => onChange(o.val)}
          style={{
            padding: "2px 10px", fontSize: 11, borderRadius: 20,
            border: `1px solid ${value === o.val ? "var(--theme-primary,#f97316)" : "var(--border-color,#444)"}`,
            background: value === o.val ? "var(--theme-primary,#f97316)" : "transparent",
            color: value === o.val ? "#fff" : "var(--text-secondary,#aaa)",
            cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─── Single Point Input ───────────────────────────────────────────────────────
function PointInput({ pointId, label, required, point, onChange, visitOptions, loadingVisits, t, onRemove }) {
  const [type, setType] = useState(point?.type || TYPES.ADDRESS);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const addrKeyRef = useRef(0);

  const handleTypeChange = (newType) => {
    setType(newType);
    setLat(""); setLng("");
    addrKeyRef.current += 1;
    onChange({ type: newType, label: "", location: "", latitude: null, longitude: null });
  };

  const handleAddressSelected = ({ address, latitude, longitude }) => {
    onChange({ type: TYPES.ADDRESS, label: address, location: address, latitude, longitude });
  };

  const handleLatLng = (field, val) => {
    const newLat = field === "lat" ? val : lat;
    const newLng = field === "lng" ? val : lng;
    if (field === "lat") setLat(val);
    else setLng(val);
    const latN = Number(newLat), lngN = Number(newLng);
    if (newLat !== "" && newLng !== "" && !isNaN(latN) && !isNaN(lngN)) {
      const location = `${newLat},${newLng}`;
      onChange({ type: TYPES.COORDINATE, label: location, location, latitude: latN, longitude: lngN });
    } else {
      onChange({ type: TYPES.COORDINATE, label: "", location: "", latitude: null, longitude: null });
    }
  };

  const handleVisit = (e) => {
    const id = Number(e.target.value);
    const opt = visitOptions.find((o) => o.markerId === id);
    if (opt) {
      const location = `${opt.latitude},${opt.longitude}`;
      onChange({ type: TYPES.VISIT_POINT, label: opt.markerDescription, location, latitude: Number(opt.latitude), longitude: Number(opt.longitude), visitPointId: opt.markerId });
    } else {
      onChange({ type: TYPES.VISIT_POINT, label: "", location: "", latitude: null, longitude: null });
    }
  };

  const isSet = !!point?.location;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary,#aaa)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {label}
          {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <TypePills value={type} onChange={handleTypeChange} t={t} />
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              title="Remover"
              style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: "2px 4px", fontSize: 11, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}
            >
              <FaTimes size={11} /> Remover
            </button>
          )}
        </div>
      </div>

      {type === TYPES.ADDRESS && (
        <AddressInput
          key={`addr-${pointId}-${addrKeyRef.current}`}
          id={`addr-${pointId}-${addrKeyRef.current}`}
          onPlaceSelected={handleAddressSelected}
        />
      )}

      {type === TYPES.COORDINATE && (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Latitude  ex: -23.5468"
            value={lat}
            onChange={(e) => handleLatLng("lat", e.target.value)}
            style={{ fontSize: 13 }}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Longitude  ex: -46.6353"
            value={lng}
            onChange={(e) => handleLatLng("lng", e.target.value)}
            style={{ fontSize: 13 }}
          />
        </div>
      )}

      {type === TYPES.VISIT_POINT && (
        <select
          className="form-control"
          defaultValue=""
          onChange={handleVisit}
          disabled={loadingVisits}
          style={{ fontSize: 13 }}
        >
          <option value="">{loadingVisits ? "Carregando pontos…" : "Selecione um ponto de visita"}</option>
          {visitOptions.map((o) => (
            <option key={o.markerId} value={o.markerId}>{o.markerDescription}</option>
          ))}
        </select>
      )}

      {isSet && (
        <div style={{ fontSize: 11, color: "#16a34a", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
          <FaCheck size={9} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{point.label}</span>
        </div>
      )}
    </div>
  );
}

// ─── Add Intermediate Button ──────────────────────────────────────────────────
function AddIntermediateButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "none",
        border: "1px dashed var(--border-color,#444)",
        borderRadius: 8, padding: "5px 14px",
        cursor: "pointer", color: "var(--text-secondary,#aaa)",
        fontSize: 12, transition: "all .15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--theme-primary,#f97316)"; e.currentTarget.style.color = "var(--theme-primary,#f97316)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-color,#444)"; e.currentTarget.style.color = "var(--text-secondary,#aaa)"; }}
    >
      <FaPlus size={10} /> Adicionar destino intermediário
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Routing() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch({ type: "title", value: t("ROUTING.TITLE") });
  }, [dispatch, t]);

  const {
    origin, setOrigin,
    destination, setDestination,
    points, addPoint, updatePoint, removePoint,
    canCalculateRoute, buildRoutingPayload, isAtMaxIntermediates,
  } = useRoutePoints();

  const { routeResult, calculating, error, calculate } = useRouteCalculation();

  const [showMapModal, setShowMapModal] = useState(false);
  const [showFenceModal, setShowFenceModal] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [visitOptions, setVisitOptions] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([{ value: DEFAULT_VEHICLE_TYPE, label: DEFAULT_VEHICLE_TYPE }]);
  const [vehicleType, setVehicleType] = useState(() =>
    normalizeVehicleTypeSelection(location.state?.vehicleType ?? location.state?.VehicleType)
  );
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);

  const [isRouting, setIsRouting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [autoSave, setAutoSave] = useState(true);

  useEffect(() => {
    setIsSaved(false);
  }, [origin, destination, points, vehicleType]);

  useEffect(() => {
    setLoadingVisits(true);
    controlService.findCoordinate(2)
      .then((res) => { if (Array.isArray(res)) setVisitOptions(res); })
      .finally(() => setLoadingVisits(false));
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingVehicleTypes(true);
    routingService.fetchVehicleTypes()
      .then((res) => {
        if (!isMounted) return;
        const options = normalizeVehicleTypeOptions(res);
        setVehicleTypeOptions(options);
        setVehicleType((currentVehicleType) => {
          const selectedExists = options.some((option) => option.value === currentVehicleType);
          return selectedExists ? currentVehicleType : options[0]?.value ?? DEFAULT_VEHICLE_TYPE;
        });
      })
      .finally(() => { if (isMounted) setLoadingVehicleTypes(false); });
    return () => { isMounted = false; };
  }, []);

  const showToast = (title, body, color = "success") =>
    setToastParams(JSON.stringify({ show: true, title, body, color }));

  const buildGoogleMapsLink = () => {
    const enc = (loc) => encodeURIComponent(loc);
    const wp = points.length ? `&waypoints=${points.map((p) => enc(toLocationString(p))).join("|")}` : "";
    const baseLink = `https://www.google.com/maps/dir/?api=1&origin=${enc(toLocationString(origin))}&destination=${enc(toLocationString(destination))}${wp}`;
    return appendTravelModeToGoogleMapsUrl(baseLink, vehicleType);
  };

  const handleAddPoint = () => {
    addPoint({ type: TYPES.ADDRESS, label: "", location: "", latitude: null, longitude: null });
  };

  const saveRoute = async (calcResult) => {
    setIsSaving(true);
    try {
      const resolvedVehicleType = calcResult?.vehicleType ?? calcResult?.VehicleType;
      if (resolvedVehicleType) {
        setVehicleType(normalizeVehicleTypeSelection(resolvedVehicleType));
      }

      const savePayload = {
        VehicleType: vehicleType,
        OriginName: origin.label,
        OriginLatitude: origin.latitude,
        OriginLongitude: origin.longitude,
        DestinationName: destination.label,
        DestinationLatitude: destination.latitude,
        DestinationLongitude: destination.longitude,
        IntermediatePoints: points.length > 0
          ? points.map((p) => toLocationString(p)).join(";")
          : "",
        MapLink: buildGoogleMapsLink(),
        Instructions: calcResult?.instructions ?? "",
      };
      const saved = await routingService.createRoute(savePayload);

      if (saved) {
        setIsSaved(true);
        showToast(t("COMMON.SUCCESS"), "Roteirização salva com sucesso");
      } else {
        showToast(t("COMMON.ERROR"), "Erro ao salvar a roteirização", "danger");
      }
      return saved;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoteirizar = async () => {
    if (!canCalculateRoute) return;
    setIsRouting(true);
    try {
      const payload = buildRoutingPayload(vehicleType);
      const result = await calculate(payload);
      if (!result) {
        showToast(t("COMMON.ERROR"), t("ROUTING.ERROR_CALCULATE_ROUTE"), "danger");
        return;
      }

      if (autoSave) {
        await saveRoute(result);
      } else {
        showToast(t("COMMON.SUCCESS"), "Roteirização calculada. Clique em Salvar para confirmar.");
      }
    } finally {
      setIsRouting(false);
    }
  };

  const handleSaveClick = async () => {
    if (!routeResult) return;
    await saveRoute(routeResult);
  };

  const handleCopyLink = () => {
    if (!isSaved) return;
    navigator.clipboard.writeText(buildGoogleMapsLink())
      .then(() => showToast(t("COMMON.SUCCESS"), t("ROUTING.LINK_COPIED")))
      .catch(() => showToast(t("COMMON.ERROR"), t("ROUTING.LINK_COPY_ERROR"), "danger"));
  };

  const inputProps = { visitOptions, loadingVisits, t };
  const divider = <div style={{ borderTop: "1px solid var(--border-color,#333)", margin: "16px 0" }} />;

  // Botão SALVAR só aparece quando: autoSave está desligado, já tem um
  // resultado calculado e ele ainda não foi salvo.
  const showManualSaveButton = !autoSave && !!routeResult && !isSaved;

  return (
    <>
      <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />

      <div className="container-xxl flex-grow-1 container-p-y">
        <div style={{ maxWidth: 640 }}>
          <div className="card" style={{ padding: "24px 28px" }}>

            {/* ── Veículo ── */}
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary,#aaa)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Veículo <span style={{ color: "#ef4444" }}>*</span>
              </span>
              <select
                className="form-control mt-1"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                disabled={loadingVehicleTypes || vehicleTypeOptions.length === 0}
                style={{ fontSize: 13 }}
              >
                {loadingVehicleTypes && (
                  <option value={vehicleType}>Carregando tipos de veículo…</option>
                )}
                {!loadingVehicleTypes && vehicleTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {divider}

            <PointInput pointId="origin" label="Origem" required point={origin} onChange={setOrigin} {...inputProps} />

            {divider}

            <PointInput pointId="destination" label="Destino" required point={destination} onChange={setDestination} {...inputProps} />

            {points.length > 0 && points.map((p, i) => (
              <React.Fragment key={p.id}>
                {divider}
                <PointInput
                  pointId={p.id}
                  label={`Parada ${String(i + 1).padStart(2, "0")}`}
                  point={p}
                  onChange={(data) => updatePoint(p.id, data)}
                  onRemove={() => removePoint(p.id)}
                  {...inputProps}
                />
              </React.Fragment>
            ))}

            {!isAtMaxIntermediates && (
              <>
                {divider}
                <AddIntermediateButton onClick={handleAddPoint} />
              </>
            )}

            {error && (
              <div className="alert alert-danger py-2 mt-3" style={{ fontSize: 13 }}>{error}</div>
            )}

            {/* ── Opção de salvar automático ── */}
            {divider}
            <div className="form-check" style={{ fontSize: 13 }}>
              <input
                className="form-check-input"
                type="checkbox"
                id="autoSaveCheck"
                checked={autoSave}
                onChange={(e) => setAutoSave(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="autoSaveCheck">
                Salvar automaticamente ao roteirizar
              </label>
            </div>

            {/* ── Actions ── */}
            <div style={{ marginTop: 12, paddingTop: 16, borderTop: "1px solid var(--border-color,#333)", display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleRoteirizar}
                disabled={!canCalculateRoute || isRouting || calculating}
                style={{ fontSize: 13 }}
              >
                {isRouting || calculating
                  ? <><span className="spinner-border spinner-border-sm" /> Roteirizando…</>
                  : <><FaRoute size={13} /> ROTEIRIZAR</>}
              </button>

              {showManualSaveButton && (
                <button
                  type="button"
                  className="btn btn-success d-flex align-items-center gap-2"
                  onClick={handleSaveClick}
                  disabled={isSaving}
                  style={{ fontSize: 13 }}
                >
                  {isSaving
                    ? <><span className="spinner-border spinner-border-sm" /> Salvando…</>
                    : <><FaSave size={13} /> SALVAR</>}
                </button>
              )}

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setShowMapModal(true)}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                {t("ROUTING.VIEW_MAP")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={handleCopyLink}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                <FaLink size={13} /> {t("ROUTING.COPY_LINK")}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => setShowFenceModal(true)}
                disabled={!isSaved}
                style={{ fontSize: 13 }}
              >
                <FaShieldAlt size={13} /> {t("ROUTING.GENERATE_FENCE")}
              </button>
            </div>

          </div>
        </div>
      </div>

      <RouteMap
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        routeResult={routeResult}
        origin={origin}
        destination={destination}
        points={points}
        vehicleType={vehicleType}
      />

      <GenerateFenceModal
        show={showFenceModal}
        onHide={() => setShowFenceModal(false)}
        routeResult={routeResult}
        origin={origin}
        destination={destination}
        onSuccess={() => showToast(t("COMMON.SUCCESS"), t("ELETRONIC_FENCES.SAVED_SUCCESS"))}
      />
    </>
  );
}
